import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import {
  CheckCircle2,
  Clock,
  FileText,
  Upload,
  Download,
  Loader2,
  CreditCard,
  FileSignature,
  Calendar,
  AlertTriangle,
  Lock,
  Users,
  FileCheck,
  Info,
  RefreshCw
} from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { getStatusLabel, getStatusColor } from "@/utils/serviceAStatus";
import EngagementTimeline from "@/components/admin/EngagementTimeline";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ServiceAAdminPanel - Admin controls for Service A engagement workflow
 * 
 * Phase 3 Improvements:
 * - Operator-friendly status labels
 * - Guardrail warnings
 * - Disabled controls with explanations
 * - Read-only activity timeline
 */
const ServiceAAdminPanel = ({ serId, onUpdate }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [uploadingTranscript, setUploadingTranscript] = useState(null);
  const [scheduleModal, setScheduleModal] = useState({ open: false, interview: null });
  const [scheduleForm, setScheduleForm] = useState({
    scheduled_start: "",
    scheduled_end: "",
    participant_names: "",
    notes_admin: ""
  });

  const fetchServiceADetails = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/ser/${serId}/service-a`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch Service A details:", error);
      toast.error("Failed to load Service A details");
    } finally {
      setLoading(false);
    }
  }, [serId, token]);

  useEffect(() => {
    if (serId) {
      fetchServiceADetails();
    }
  }, [serId, fetchServiceADetails]);

  const handleAcceptAgreement = async () => {
    try {
      await axios.post(`${API}/admin/ser/${serId}/accept-agreement`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Agreement accepted");
      fetchServiceADetails();
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept agreement");
    }
  };

  const handleMockPayment = async (success = true) => {
    try {
      await axios.post(`${API}/admin/ser/${serId}/mock-payment`, {
        ser_id: serId,
        simulate_success: success
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(success ? "Payment simulated successfully" : "Payment failure simulated");
      fetchServiceADetails();
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to simulate payment");
    }
  };

  const handleInitInterviews = async () => {
    try {
      const response = await axios.post(`${API}/admin/ser/${serId}/interviews/init`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.already_existed) {
        toast.info("Interviews already initialized");
      } else {
        toast.success("Interview slots created");
      }
      fetchServiceADetails();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object') {
        toast.error(detail.message || "Failed to initialize interviews");
      } else {
        toast.error(detail || "Failed to initialize interviews");
      }
    }
  };

  const handleScheduleInterview = async () => {
    const interview = scheduleModal.interview;
    if (!interview) return;

    try {
      const payload = {};
      if (scheduleForm.scheduled_start) {
        payload.scheduled_start = new Date(scheduleForm.scheduled_start).toISOString();
      }
      if (scheduleForm.scheduled_end) {
        payload.scheduled_end = new Date(scheduleForm.scheduled_end).toISOString();
      }
      if (scheduleForm.participant_names) {
        payload.participant_names = scheduleForm.participant_names.split(",").map(n => n.trim());
      }
      if (scheduleForm.notes_admin) {
        payload.notes_admin = scheduleForm.notes_admin;
      }
      payload.status = "SCHEDULED";

      await axios.patch(`${API}/admin/interviews/${interview.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Interview scheduled");
      setScheduleModal({ open: false, interview: null });
      setScheduleForm({ scheduled_start: "", scheduled_end: "", participant_names: "", notes_admin: "" });
      fetchServiceADetails();
    } catch (error) {
      toast.error("Failed to schedule interview");
    }
  };

  const handleMarkInterviewComplete = async (interviewId) => {
    try {
      await axios.patch(`${API}/admin/interviews/${interviewId}`, {
        status: "COMPLETED"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Interview marked as completed");
      fetchServiceADetails();
    } catch (error) {
      toast.error("Failed to mark interview complete");
    }
  };

  const handleTranscriptUpload = async (interviewId, file) => {
    setUploadingTranscript(interviewId);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${API}/admin/interviews/${interviewId}/transcript`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success("Transcript uploaded successfully");
      fetchServiceADetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload transcript");
    } finally {
      setUploadingTranscript(null);
    }
  };

  const handleGenerateFinalReport = async (forceRegenerate = false) => {
    setGeneratingReport(true);
    try {
      // Use async endpoint for report generation (Phase 6)
      const response = await axios.post(
        `${API}/admin/ser/${serId}/service-a/report/generate-async?force_regenerate=${forceRegenerate}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.already_exists && !forceRegenerate) {
        toast.info("Report already exists. Click regenerate to create a new version.");
        setGeneratingReport(false);
        return;
      }
      
      // Start polling for job completion
      const jobId = response.data.job_id;
      toast.info("Report generation started. This may take a minute...");
      
      const pollJob = async (attempts = 0) => {
        if (attempts >= 60) {
          toast.error("Report generation timed out. Please try again.");
          setGeneratingReport(false);
          return;
        }
        
        try {
          const jobResponse = await axios.get(
            `${API}/jobs/${jobId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          const jobStatus = jobResponse.data.status;
          const progressStage = jobResponse.data.progress_stage;
          
          if (jobStatus === "SUCCEEDED") {
            toast.success(`Discovery & Viability Assessment report generated (v${jobResponse.data.result?.version || 1})`);
            setGeneratingReport(false);
            fetchServiceADetails();
            onUpdate?.();
            return;
          }
          
          if (jobStatus === "FAILED") {
            const errorMsg = jobResponse.data.error?.message || "Report generation failed";
            toast.error(errorMsg);
            setGeneratingReport(false);
            return;
          }
          
          // Still running - update progress message
          if (progressStage && attempts % 5 === 0) {
            toast.info(`Progress: ${progressStage}`, { id: "report-progress" });
          }
          
          // Continue polling
          setTimeout(() => pollJob(attempts + 1), 2000);
        } catch (err) {
          console.error("Job poll error:", err);
          setTimeout(() => pollJob(attempts + 1), 3000);
        }
      };
      
      pollJob();
      
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object') {
        toast.error(detail.message || "Failed to generate report");
      } else {
        toast.error(detail || "Failed to generate report");
      }
      setGeneratingReport(false);
    }
  };

  const handleGenerateQuestionnaire = async (force = false) => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/admin/ser/${serId}/questionnaire/generate?force_regenerate=${force}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.already_exists && !force) {
        toast.info("Questionnaire already exists. Click regenerate to create a new version.");
      } else {
        toast.success(`Questionnaire generated (v${response.data.version})`);
      }
      fetchServiceADetails();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object') {
        toast.error(detail.message || "Failed to generate questionnaire");
      } else {
        toast.error(detail || "Failed to generate questionnaire");
      }
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center text-slate-400 p-8">
        Failed to load Service A details
      </div>
    );
  }

  const { ser, interviews, artifacts, gates } = data;
  const agreement = ser?.agreement || {};
  const payment = ser?.payment || {};
  const questionnaire = ser?.questionnaire || {};
  const currentStatus = ser?.service_a_status || ser?.lifecycle_state || "UNKNOWN";

  return (
    <div className="space-y-6" data-testid="service-a-admin-panel">
      {/* Status Overview with Operator-Friendly Label */}
      <Card className="bg-navy-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-primary" />
            Service A Workflow Status
          </CardTitle>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-sm text-slate-400">Current status:</span>
            <Badge className={`${getStatusColor(currentStatus)}`}>
              {getStatusLabel(currentStatus)}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* Guardrail Warning - Evidence Gate */}
          {!gates.evidence_complete && payment.status === "SUCCEEDED" && (
            <Alert className="bg-amber-500/10 border-amber-500/30">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <AlertDescription className="text-amber-200">
                <strong>Evidence collection required:</strong> Final report cannot be generated until 
                2 interviews are completed AND 2 transcripts are uploaded.
                <span className="block text-xs text-amber-400 mt-1">
                  Current: {gates.evidence_counters?.interviews_completed || 0}/2 interviews, {gates.evidence_counters?.transcripts_uploaded || 0}/2 transcripts
                </span>
              </AlertDescription>
            </Alert>
          )}

          {/* Agreement Section */}
          <div className="p-4 rounded-lg bg-navy-950/50 border border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileSignature className={`w-5 h-5 ${agreement.accepted_at ? "text-green-400" : "text-slate-500"}`} />
                <div>
                  <p className="text-white font-medium">Agreement</p>
                  <p className="text-sm text-slate-400">
                    {agreement.accepted_at 
                      ? `Accepted: ${new Date(agreement.accepted_at).toLocaleString()}`
                      : "Not yet accepted"
                    }
                  </p>
                </div>
              </div>
              {!agreement.accepted_at && (
                <Button 
                  size="sm" 
                  onClick={handleAcceptAgreement}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="accept-agreement-btn"
                >
                  Accept Agreement
                </Button>
              )}
              {agreement.accepted_at && (
                <CheckCircle2 className="w-5 h-5 text-green-400" />
              )}
            </div>
          </div>

          {/* Payment Section */}
          <div className="p-4 rounded-lg bg-navy-950/50 border border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CreditCard className={`w-5 h-5 ${payment.status === "SUCCEEDED" ? "text-green-400" : "text-slate-500"}`} />
                <div>
                  <p className="text-white font-medium">Payment</p>
                  <p className="text-sm text-slate-400">
                    {payment.status === "SUCCEEDED" 
                      ? `Paid: $${(payment.amount_cents / 100).toLocaleString()} on ${new Date(payment.paid_at).toLocaleDateString()}`
                      : `Status: ${payment.status || "PENDING"}`
                    }
                  </p>
                  {payment.provider === "mock" && payment.status === "SUCCEEDED" && (
                    <p className="text-xs text-amber-400">(Mock payment - development only)</p>
                  )}
                </div>
              </div>
              {payment.status !== "SUCCEEDED" && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button 
                          size="sm" 
                          onClick={() => handleMockPayment(true)}
                          disabled={!agreement.accepted_at}
                          className="bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="mock-payment-btn"
                        >
                          Simulate Payment
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {!agreement.accepted_at && (
                      <TooltipContent>
                        <p>Agreement must be accepted first</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              )}
              {payment.status === "SUCCEEDED" && (
                <CheckCircle2 className="w-5 h-5 text-green-400" />
              )}
            </div>
          </div>

          {/* Questionnaire Section */}
          <div className="p-4 rounded-lg bg-navy-950/50 border border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className={`w-5 h-5 ${questionnaire.generated ? "text-green-400" : "text-slate-500"}`} />
                <div>
                  <p className="text-white font-medium">Interview Questionnaire</p>
                  <p className="text-sm text-slate-400">
                    {questionnaire.generated 
                      ? `Generated: v${questionnaire.version} on ${new Date(questionnaire.generated_at).toLocaleDateString()}`
                      : gates.can_generate_questionnaire 
                        ? "Ready to generate"
                        : `Blocked: ${gates.questionnaire_reason}`
                    }
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                {questionnaire.generated && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const artifact = artifacts.find(a => a.id === questionnaire.artifact_id);
                      if (artifact?.download_url) {
                        window.open(`${process.env.REACT_APP_BACKEND_URL}${artifact.download_url}`, "_blank");
                      }
                    }}
                    data-testid="download-questionnaire-btn"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Download
                  </Button>
                )}
                {gates.can_generate_questionnaire && (
                  <>
                    {questionnaire.generated && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleGenerateQuestionnaire(true)}
                              disabled={generating}
                              data-testid="regenerate-questionnaire-btn"
                            >
                              {generating ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="font-medium">Regenerate Questionnaire</p>
                            <p className="text-xs text-slate-400">This will create a new version (v{questionnaire.version + 1})</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    {!questionnaire.generated && (
                      <Button
                        size="sm"
                        onClick={() => handleGenerateQuestionnaire(false)}
                        disabled={generating}
                        className="bg-primary hover:bg-primary/90"
                        data-testid="generate-questionnaire-btn"
                      >
                        {generating ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <FileText className="w-4 h-4 mr-1" />
                        )}
                        Generate
                      </Button>
                    )}
                  </>
                )}
                {!gates.can_generate_questionnaire && !questionnaire.generated && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Lock className="w-5 h-5 text-slate-500" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{gates.questionnaire_reason}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>
            
            {/* Regeneration Warning */}
            {questionnaire.generated && (
              <div className="mt-3 flex items-start gap-2 p-2 rounded bg-amber-500/5 border border-amber-500/20">
                <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-300">
                  Regenerating the questionnaire will create a new version. Previous versions are retained for audit purposes.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Interviews Section */}
      <Card className="bg-navy-900/50 border-slate-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Interviews ({interviews?.length || 0}/2)
            </CardTitle>
            {interviews?.length === 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button 
                        size="sm" 
                        onClick={handleInitInterviews}
                        disabled={!gates.can_schedule_interviews}
                        className="disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="init-interviews-btn"
                      >
                        Initialize Interview Slots
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {!gates.can_schedule_interviews && (
                    <TooltipContent>
                      <p>{gates.schedule_reason}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <CardDescription>
            {gates.can_schedule_interviews 
              ? "Ready to schedule interviews"
              : <span className="text-amber-400">Blocked: {gates.schedule_reason}</span>
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {interviews && interviews.length > 0 ? (
            <div className="space-y-4">
              {interviews.map((interview) => (
                <InterviewCard
                  key={interview.id}
                  interview={interview}
                  artifacts={artifacts}
                  uploadingTranscript={uploadingTranscript}
                  onSchedule={() => {
                    setScheduleModal({ open: true, interview });
                    setScheduleForm({
                      scheduled_start: interview.scheduled_start ? new Date(interview.scheduled_start).toISOString().slice(0, 16) : "",
                      scheduled_end: interview.scheduled_end ? new Date(interview.scheduled_end).toISOString().slice(0, 16) : "",
                      participant_names: interview.participant_names?.join(", ") || "",
                      notes_admin: interview.notes_admin || ""
                    });
                  }}
                  onMarkComplete={() => handleMarkInterviewComplete(interview.id)}
                  onUploadTranscript={(file) => handleTranscriptUpload(interview.id, file)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              {gates.can_schedule_interviews 
                ? "Click 'Initialize Interview Slots' to create interview slots"
                : "Complete agreement and payment to enable interviews"
              }
            </div>
          )}
        </CardContent>
      </Card>

      {/* Evidence Gate Status */}
      <Card className="bg-navy-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <AlertTriangle className={`w-5 h-5 ${gates.evidence_complete ? "text-green-400" : "text-amber-400"}`} />
            Evidence Gate
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 rounded-lg bg-navy-950/50">
              <p className="text-sm text-slate-400">Interviews Completed</p>
              <p className={`text-2xl font-bold ${(gates.evidence_counters?.interviews_completed || 0) >= 2 ? "text-green-400" : "text-white"}`}>
                {gates.evidence_counters?.interviews_completed || 0} / 2
              </p>
            </div>
            <div className="p-3 rounded-lg bg-navy-950/50">
              <p className="text-sm text-slate-400">Transcripts Uploaded</p>
              <p className={`text-2xl font-bold ${(gates.evidence_counters?.transcripts_uploaded || 0) >= 2 ? "text-green-400" : "text-white"}`}>
                {gates.evidence_counters?.transcripts_uploaded || 0} / 2
              </p>
            </div>
          </div>
          <div className="mt-4 p-3 rounded-lg border border-slate-700">
            <p className={`text-sm font-medium ${gates.evidence_complete ? "text-green-400" : "text-amber-400"}`}>
              {gates.evidence_complete 
                ? "✓ Evidence collection complete – Ready for final report generation"
                : "⏳ Evidence collection in progress – Complete all interviews and upload transcripts to proceed"
              }
            </p>
          </div>
          
          {/* Generate Report Button (disabled until evidence complete) */}
          <div className="mt-4 flex justify-end gap-2">
            {/* Show download button if report exists */}
            {ser?.final_report?.artifact_id && (
              <Button
                variant="outline"
                onClick={() => {
                  const reportArtifact = artifacts?.find(a => a.id === ser.final_report.artifact_id);
                  if (reportArtifact?.download_url) {
                    window.open(`${process.env.REACT_APP_BACKEND_URL}${reportArtifact.download_url}`, "_blank");
                  }
                }}
                data-testid="download-report-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Report v{ser.final_report.version || 1}
              </Button>
            )}
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      onClick={() => handleGenerateFinalReport(!!ser?.final_report?.artifact_id)}
                      disabled={!gates.evidence_complete || generatingReport}
                      className="disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="generate-report-btn"
                    >
                      {generatingReport ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : ser?.final_report?.artifact_id ? (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      ) : (
                        <FileText className="w-4 h-4 mr-2" />
                      )}
                      {generatingReport 
                        ? "Generating..." 
                        : ser?.final_report?.artifact_id 
                          ? "Regenerate Report" 
                          : "Generate Final Report"
                      }
                    </Button>
                  </span>
                </TooltipTrigger>
                {!gates.evidence_complete && (
                  <TooltipContent>
                    <p className="font-medium">Evidence incomplete</p>
                    <p className="text-xs text-slate-400">
                      Requires: {2 - (gates.evidence_counters?.interviews_completed || 0)} more interview(s), {" "}
                      {2 - (gates.evidence_counters?.transcripts_uploaded || 0)} more transcript(s)
                    </p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardContent>
      </Card>

      {/* Activity Timeline (Read-Only Ledger) */}
      <EngagementTimeline ser={ser} interviews={interviews} artifacts={artifacts} />

      {/* Schedule Interview Modal */}
      <Dialog open={scheduleModal.open} onOpenChange={(open) => !open && setScheduleModal({ open: false, interview: null })}>
        <DialogContent className="bg-navy-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">
              Schedule Interview {scheduleModal.interview?.ordinal}
            </DialogTitle>
            <DialogDescription>
              Set the date, time, and participants for this interview.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Start Time</Label>
                <Input
                  type="datetime-local"
                  value={scheduleForm.scheduled_start}
                  onChange={(e) => setScheduleForm(f => ({ ...f, scheduled_start: e.target.value }))}
                  className="bg-navy-950 border-slate-700 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">End Time</Label>
                <Input
                  type="datetime-local"
                  value={scheduleForm.scheduled_end}
                  onChange={(e) => setScheduleForm(f => ({ ...f, scheduled_end: e.target.value }))}
                  className="bg-navy-950 border-slate-700 text-white"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Participants (comma-separated)</Label>
              <Input
                value={scheduleForm.participant_names}
                onChange={(e) => setScheduleForm(f => ({ ...f, participant_names: e.target.value }))}
                placeholder="John Smith, Jane Doe"
                className="bg-navy-950 border-slate-700 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-300">Admin Notes</Label>
              <Textarea
                value={scheduleForm.notes_admin}
                onChange={(e) => setScheduleForm(f => ({ ...f, notes_admin: e.target.value }))}
                placeholder="Internal notes about this interview..."
                className="bg-navy-950 border-slate-700 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleModal({ open: false, interview: null })}>
              Cancel
            </Button>
            <Button onClick={handleScheduleInterview} className="bg-primary hover:bg-primary/90">
              Schedule Interview
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/**
 * InterviewCard - Individual interview slot display
 */
const InterviewCard = ({ interview, artifacts, uploadingTranscript, onSchedule, onMarkComplete, onUploadTranscript }) => {
  const statusLabels = {
    DRAFT: "Not Scheduled",
    SCHEDULED: "Scheduled",
    COMPLETED: "Completed",
    CANCELLED: "Cancelled"
  };
  
  const statusColors = {
    DRAFT: "bg-slate-500/20 text-slate-400 border-slate-500/30",
    SCHEDULED: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    COMPLETED: "bg-green-500/20 text-green-400 border-green-500/30",
    CANCELLED: "bg-red-500/20 text-red-400 border-red-500/30"
  };

  const transcript = artifacts?.find(a => a.type === "transcript" && a.metadata?.interview_id === interview.id);

  return (
    <div className="p-4 rounded-lg bg-navy-950/50 border border-slate-700" data-testid={`interview-card-${interview.ordinal}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-white font-medium">Interview {interview.ordinal}</p>
            <Badge className={statusColors[interview.status]}>{statusLabels[interview.status]}</Badge>
          </div>
          {interview.scheduled_start && (
            <p className="text-sm text-slate-400 mt-1">
              <Clock className="w-3 h-3 inline mr-1" />
              {new Date(interview.scheduled_start).toLocaleString()}
            </p>
          )}
          {interview.participant_names?.length > 0 && (
            <p className="text-sm text-slate-400">
              <Users className="w-3 h-3 inline mr-1" />
              {interview.participant_names.join(", ")}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {interview.status === "DRAFT" && (
            <Button size="sm" variant="outline" onClick={onSchedule}>
              <Calendar className="w-4 h-4 mr-1" />
              Schedule
            </Button>
          )}
          {interview.status === "SCHEDULED" && (
            <>
              <Button size="sm" variant="outline" onClick={onSchedule}>
                Edit
              </Button>
              <Button size="sm" onClick={onMarkComplete} className="bg-green-600 hover:bg-green-700">
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Complete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Transcript Section */}
      <div className="mt-3 pt-3 border-t border-slate-700">
        <p className="text-sm text-slate-400 mb-2">Transcript</p>
        {transcript ? (
          <div className="flex items-center justify-between p-2 bg-green-500/10 rounded border border-green-500/30">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-green-400" />
              <span className="text-sm text-green-400">{transcript.filename}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${transcript.download_url}`, "_blank")}
            >
              <Download className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 px-3 py-2 rounded border border-dashed border-slate-600 cursor-pointer hover:border-primary transition-colors">
              {uploadingTranscript === interview.id ? (
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
              ) : (
                <Upload className="w-4 h-4 text-slate-400" />
              )}
              <span className="text-sm text-slate-400">
                {uploadingTranscript === interview.id ? "Uploading..." : "Upload transcript"}
              </span>
              <input
                type="file"
                className="hidden"
                accept=".txt,.pdf,.docx"
                disabled={uploadingTranscript === interview.id}
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    onUploadTranscript(e.target.files[0]);
                  }
                }}
              />
            </label>
            <span className="text-xs text-slate-500">.txt, .pdf, .docx</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceAAdminPanel;
